import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_SETTINGS,
  describeCompatReason,
  MAX_PRESET_DESCRIPTION_LENGTH,
  MAX_PRESET_NAME_LENGTH,
  type LobbySettings,
  type PresetCompat,
} from '@srcbattle/shared';
import {
  api,
  ApiRequestError,
  type PresetSort,
  type SettingsPreset,
} from '../api.ts';
import { Banner, Button } from './ui.tsx';

/**
 * The version badge.
 *
 * Green means the saved snapshot still matches this build's settings page; red
 * means it does not, and the tooltip says which way. Deliberately a small
 * inline stamp rather than a banner: on a list of twenty presets, twenty
 * warnings would be wallpaper, but twenty badges of which two are red reads at
 * a glance.
 */
function VersionBadge({ version, compat }: { version: string; compat: PresetCompat }) {
  const ok = compat.status === 'ok';
  return (
    <span
      title={describeCompatReason(compat)}
      className={`shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] ${
        ok ? 'bg-success/15 text-success' : 'bg-danger/15 text-danger'
      }`}
    >
      {version || 'unversioned'}
      {!ok && ' ⚠'}
    </span>
  );
}

function Stars({
  rating,
  count,
  mine,
  onRate,
  disabled,
}: {
  rating: number | null;
  count: number;
  mine: number | null;
  onRate?: (rating: number) => void;
  disabled?: boolean;
}) {
  const filled = mine ?? Math.round(rating ?? 0);

  return (
    <span className="flex items-center gap-1">
      <span className="flex" role={onRate ? 'group' : undefined} aria-label="Rating">
        {[1, 2, 3, 4, 5].map((star) =>
          onRate ? (
            <button
              key={star}
              type="button"
              disabled={disabled}
              onClick={() => onRate(star)}
              aria-label={`Rate ${star} out of 5`}
              aria-pressed={mine === star}
              className={`px-0.5 text-sm leading-none transition-colors disabled:opacity-50 ${
                star <= filled ? 'text-warning' : 'text-text-faint hover:text-warning'
              }`}
            >
              ★
            </button>
          ) : (
            <span key={star} aria-hidden className={star <= filled ? 'text-warning' : 'text-text-faint'}>
              ★
            </span>
          ),
        )}
      </span>
      <span className="text-[11px] text-text-faint">
        {rating === null ? 'unrated' : `${rating.toFixed(1)} · ${count}`}
        {mine !== null && ' · yours'}
      </span>
    </span>
  );
}

/** The publish form, which is also the edit form once a preset is published. */
function PublishForm({
  preset,
  busy,
  onPublish,
  onCancel,
}: {
  preset: SettingsPreset;
  busy: boolean;
  onPublish: (input: { name: string; description: string }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(preset.name);
  const [description, setDescription] = useState(preset.description);

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-border bg-surface-raised p-2">
      <label className="block text-xs text-text-muted">
        Title
        <input
          value={name}
          maxLength={MAX_PRESET_NAME_LENGTH}
          onChange={(event) => setName(event.target.value)}
          className="mt-1 w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text"
        />
      </label>
      <label className="block text-xs text-text-muted">
        Description
        <textarea
          value={description}
          rows={2}
          maxLength={MAX_PRESET_DESCRIPTION_LENGTH}
          placeholder="What kind of night is this for?"
          onChange={(event) => setDescription(event.target.value)}
          className="mt-1 w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text"
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button
          disabled={busy || !name.trim() || !description.trim()}
          onClick={() => onPublish({ name: name.trim(), description: description.trim() })}
        >
          {preset.isPublished ? 'Save changes' : 'Publish'}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function PresetRow({
  preset,
  busy,
  canApply,
  onApply,
  onRate,
  onPublish,
  onUnpublish,
  onOverwrite,
  onReconcile,
  onDelete,
}: {
  preset: SettingsPreset;
  busy: boolean;
  canApply: boolean;
  onApply: () => void;
  onRate?: (rating: number) => void;
  onPublish?: (input: { name: string; description: string }) => void;
  onUnpublish?: () => void;
  onOverwrite?: () => void;
  onReconcile?: () => void;
  onDelete?: () => void;
}) {
  const [publishing, setPublishing] = useState(false);

  return (
    <li className="px-1 py-2">
      <div className="flex flex-wrap items-start gap-2">
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium">{preset.name}</span>
            <VersionBadge version={preset.appVersion} compat={preset.compat} />
            {preset.isPublished && (
              <span className="rounded bg-accent/15 px-1.5 py-0.5 text-[10px] text-accent">published</span>
            )}
          </span>
          {preset.description && (
            <span className="mt-0.5 block text-xs text-text-faint">{preset.description}</span>
          )}
          <span className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-text-faint">
            <span>by {preset.isMine ? 'you' : preset.authorName}</span>
            <Stars
              rating={preset.averageRating}
              count={preset.ratingCount}
              mine={preset.myRating}
              disabled={busy}
              {...(onRate ? { onRate } : {})}
            />
          </span>
        </span>

        {canApply && (
          <Button variant="secondary" className="px-3 py-1.5" disabled={busy} onClick={onApply}>
            Use
          </Button>
        )}
      </div>

      {preset.compat.status === 'conflict' && (
        <p className="mt-1 text-[11px] text-danger">
          {describeCompatReason(preset.compat)}
          {onReconcile && (
            <button
              type="button"
              disabled={busy}
              onClick={onReconcile}
              className="ml-2 underline underline-offset-2 disabled:opacity-50"
            >
              Update it to this version
            </button>
          )}
        </p>
      )}

      {(onOverwrite || onPublish || onDelete) && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {onOverwrite && (
            <Button variant="ghost" className="px-2 py-1 text-xs" disabled={busy} onClick={onOverwrite}>
              Save this lobby's settings into it
            </Button>
          )}
          {onPublish && (
            <Button
              variant="ghost"
              className="px-2 py-1 text-xs"
              disabled={busy}
              onClick={() => setPublishing((open) => !open)}
            >
              {preset.isPublished ? 'Edit listing' : 'Publish'}
            </Button>
          )}
          {onUnpublish && preset.isPublished && (
            <Button variant="ghost" className="px-2 py-1 text-xs" disabled={busy} onClick={onUnpublish}>
              Unpublish
            </Button>
          )}
          {onDelete && (
            <Button variant="ghost" className="px-2 py-1 text-xs" disabled={busy} onClick={onDelete}>
              Delete
            </Button>
          )}
        </div>
      )}

      {publishing && onPublish && (
        <PublishForm
          preset={preset}
          busy={busy}
          onPublish={(input) => {
            onPublish(input);
            setPublishing(false);
          }}
          onCancel={() => setPublishing(false)}
        />
      )}
    </li>
  );
}

/**
 * Saved settings, on the lobby screen.
 *
 * Three things in one collapsed drawer, because they are one task: the presets
 * you can apply right now, a way to save what is currently set up, and the
 * club's published ones. The browser is a sub-drawer rather than a separate
 * page — going somewhere else to find a preset and then coming back to apply it
 * is the version of this that nobody would use.
 */
export function SettingsPresets({
  lobbyId,
  settings,
  isHost,
  onApplied,
}: {
  lobbyId: string;
  settings: LobbySettings;
  isHost: boolean;
  onApplied: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState<SettingsPreset[] | null>(null);
  const [published, setPublished] = useState<SettingsPreset[] | null>(null);
  const [browsing, setBrowsing] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<PresetSort>('rating');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const loadMine = useCallback(async () => {
    const { presets } = await api.presets();
    setMine(presets);
  }, []);

  const loadPublished = useCallback(async () => {
    const { presets } = await api.publishedPresets({ query, sort });
    setPublished(presets);
  }, [query, sort]);

  useEffect(() => {
    if (!open) return;
    void loadMine().catch(() => setError('Could not load your presets.'));
  }, [open, loadMine]);

  // Re-runs on every search keystroke and sort change; the list is club-sized,
  // so filtering on the server keeps the two orderings in one place.
  useEffect(() => {
    if (!open || !browsing) return;
    void loadPublished().catch(() => setError('Could not load published presets.'));
  }, [open, browsing, loadPublished]);

  async function run(work: () => Promise<unknown>, message?: string) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await work();
      await loadMine();
      if (browsing) await loadPublished();
      if (message) setNote(message);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const apply = (preset: SettingsPreset | null) =>
    run(async () => {
      if (preset) await api.applyPreset(lobbyId, preset.id);
      else await api.updateSettings(lobbyId, DEFAULT_SETTINGS);
      onApplied();
    }, preset ? `Applied “${preset.name}”.` : 'Back to the built-in defaults.');

  return (
    <section className="mt-3 rounded-xl border border-border">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((shown) => !shown)}
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
      >
        <span className="min-w-0">
          <span className="block text-xs font-semibold uppercase tracking-wider text-accent">
            Settings presets
          </span>
        </span>
        <span aria-hidden className="shrink-0 text-xs text-text-faint">
          {open ? '−' : '+'}
        </span>
      </button>

      {open && (
        <div className="border-t border-border p-3">
          {error && (
            <div className="mb-2">
              <Banner tone="danger">{error}</Banner>
            </div>
          )}
          {note && !error && (
            <div className="mb-2">
              <Banner tone="success">{note}</Banner>
            </div>
          )}
          {!isHost && (
            <p className="mb-2 text-xs text-text-faint">
              Only the host can apply presets.
            </p>
          )}

          <ul className="divide-y divide-border">
            {/* Always first, always there, and not a saved row: this is the
                build's own defaults, not somebody's copy of them. */}
            <li className="px-1 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">Default</span>
                  <span className="block text-xs text-text-faint">
                    Built-in settings.
                  </span>
                </span>
                {isHost && (
                  <Button
                    variant="secondary"
                    className="px-3 py-1.5"
                    disabled={busy}
                    onClick={() => void apply(null)}
                  >
                    Use
                  </Button>
                )}
              </div>
            </li>

            {mine?.map((preset) => (
              <PresetRow
                key={preset.id}
                preset={preset}
                busy={busy}
                canApply={isHost}
                onApply={() => void apply(preset)}
                onOverwrite={() =>
                  void run(
                    () => api.updatePreset(preset.id, { settings }),
                    `Saved this lobby's settings into “${preset.name}”.`,
                  )
                }
                onPublish={(input) =>
                  void run(
                    () => api.publishPreset(preset.id, { published: true, ...input }),
                    `“${input.name}” is published.`,
                  )
                }
                onUnpublish={() =>
                  void run(() => api.publishPreset(preset.id, { published: false }), 'Taken down.')
                }
                {...(preset.compat.status === 'conflict'
                  ? { onReconcile: () => void run(() => api.reconcilePreset(preset.id), 'Preset updated.') }
                  : {})}
                onDelete={() => {
                  if (!window.confirm(`Delete “${preset.name}”? This cannot be undone.`)) return;
                  void run(() => api.deletePreset(preset.id), 'Preset deleted.');
                }}
              />
            ))}
          </ul>

          {mine !== null && mine.length === 0 && (
            <p className="px-1 py-2 text-xs text-text-faint">
              No saved presets yet.
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-border pt-3">
            <label className="min-w-0 flex-1 text-xs text-text-muted">
              Save the current settings as
              <input
                value={newName}
                maxLength={MAX_PRESET_NAME_LENGTH}
                placeholder="Friday night usual"
                onChange={(event) => setNewName(event.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text"
              />
            </label>
            <Button
              disabled={busy || !newName.trim()}
              onClick={() =>
                void run(async () => {
                  await api.createPreset({ name: newName.trim(), settings });
                  setNewName('');
                }, 'Preset saved.')
              }
            >
              Save preset
            </Button>
          </div>

          <div className="mt-3 border-t border-border pt-3">
            <Button
              variant="ghost"
              className="px-2 py-1 text-xs"
              aria-expanded={browsing}
              onClick={() => setBrowsing((shown) => !shown)}
            >
              {browsing ? 'Hide the published presets' : 'Browse published presets'}
            </Button>

            {browsing && (
              <div className="mt-2">
                <div className="mb-2 grid gap-2 sm:grid-cols-[1fr_auto]">
                  <input
                    type="search"
                    aria-label="Search published presets"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search"
                    className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text"
                  />
                  <label className="text-xs text-text-muted">
                    <span className="sr-only">Sort by</span>
                    <select
                      aria-label="Sort published presets"
                      value={sort}
                      onChange={(event) => setSort(event.target.value as PresetSort)}
                      className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text"
                    >
                      <option value="rating">Best rated</option>
                      <option value="newest">Newest</option>
                      <option value="name">Title (A–Z)</option>
                      <option value="author">Who made it</option>
                    </select>
                  </label>
                </div>

                <ul className="max-h-80 divide-y divide-border overflow-y-auto">
                  {published?.map((preset) => (
                    <PresetRow
                      key={preset.id}
                      preset={preset}
                      busy={busy}
                      canApply={isHost}
                      onApply={() => void apply(preset)}
                      {...(preset.isMine
                        ? {}
                        : { onRate: (rating: number) => void run(() => api.ratePreset(preset.id, rating)) })}
                      {...(preset.isMine && preset.compat.status === 'conflict'
                        ? { onReconcile: () => void run(() => api.reconcilePreset(preset.id), 'Preset updated.') }
                        : {})}
                    />
                  ))}
                  {published !== null && published.length === 0 && (
                    <li className="p-3 text-sm text-text-muted">
                      {query ? 'Nothing matches that search.' : 'Nobody has published a preset yet.'}
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
